import i18n from '../i18n/fr';
import datas from './_datas';
import formatters from '../helpers/charts_formatter_helper';
import { toFloat, toLocaleNumber } from '../helpers/math_helper';

import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting';

HighchartsExporting(Highcharts);
HighchartsOfflineExporting(Highcharts);

const t = i18n[window.locale];

const colors       = ['#ffa100', '#93c635', '#603422', '#ecd000', '#008c8b', '#676ccf', '#f45b5b', '#aaaaaa'];
const nonEnRColors = ['#333333', '#aaaaaa', '#ff0000', '#794400'];
const enRColors    = ['#008809', '#ffe943', '#0081ff', '#00e01e'];
const energyColors = {
  oil           : '#333333',
  gaz_b1        : '#aaaaaa',
  propane       : '#ff0000',
  electricity   : '#0081ff',
  wood          : '#008809',
  bulk_granules : '#00e01e',
  bag_granules  : '#ffe943',
  shredded_wood : '#794400',
};

//------------------------------------------------------------------------------
//  PERIOD TOTAL COST
//------------------------------------------------------------------------------

function _buildPeriodTotalCostSeries(values) {
  let stackLabels        = [];
  let solarSavingDatas   = [];
  let energyCostDatas    = [];
  let investmentDatas    = [];
  let maintenanceDatas   = [];
  let enrProdRatios      = [];

  values.forEach( value => {
    const rawLabel    = _buildPeriodTotalCostSystemsStackLabel(formatters.systemLabel(value.label, 'raw'), value.enrProdRatio);
    const styledLabel = _buildPeriodTotalCostSystemsStackLabel(formatters.systemLabel(value.label, 'styled'), value.enrProdRatio);
    stackLabels.push(rawLabel + '|' + styledLabel);
    solarSavingDatas.push(value.periodSolarSaving);
    energyCostDatas.push(value.periodEnergyCost);
    investmentDatas.push(value.totalInvestment);
    maintenanceDatas.push(value.periodMaintenance);
    enrProdRatios.push(value.enrProdRatio);
  });

  return {
    stackLabels        : stackLabels,
    solarSavingDatas   : solarSavingDatas,
    energyCostDatas    : energyCostDatas,
    investmentDatas    : investmentDatas,
    maintenanceDatas   : maintenanceDatas,
    enrProdRatios      : enrProdRatios,
  };
}

function _buildPeriodTotalCostSystemsStackLabel(label, enrProdRatio) {
  let enrLabel       = "";
  let enrColor       = "";
  if (enrProdRatio == 0) {
    enrLabel = t.charts.period_total_cost.series.xAxis.non_enr;
    enrColor = "#ff0000";
  } else if (enrProdRatio < 0.5 ) {
    enrLabel = t.charts.period_total_cost.series.xAxis.mostly_non_enr;
    enrColor = "#ffa100";
  } else if (enrProdRatio < 1) {
    enrLabel = t.charts.period_total_cost.series.xAxis.mostly_enr;
    enrColor = "#93c635";
  } else {
    enrLabel = t.charts.period_total_cost.series.xAxis.enr;
    enrColor = "#008809";
  }
  /* !!! UGLY !!! */
  // Use span, br tags and inline styles because the SVG export of highcharts
  //  doesn't support some HTML tags and CSS styles (exporting.allowHTML still buggy)
  return (
    label + '<br/><b>-</b><br/>' +
    '<span style="font-size: 1.2em; font-weight: bold; color: ' + enrColor + ';">' + enrLabel + '</span>'
  );
}

export function periodTotalCost(scenario, values) {
  const series = _buildPeriodTotalCostSeries(values);

  return Highcharts.chart(scenario + '-period-total-cost-chart', {
		chart: {
			type: 'column',
      spacingTop: 0,
      spacingBottom: 0,
		},
		credits: {
	    enabled: false,
	  },
    exporting: {
      enabled: false,
    },
		title: {
			text: null,
		},
		plotOptions: {
			column: {
				stacking: 'normal',
				dataLabels: {
					enabled  : true,
          overflow : true,
          formatter: function() {
            return formatters.periodTotalCost.plotOptions(this);
          },
					color: 'white',
				},
			},
		},
		xAxis: {
      categories: series.stackLabels,
      labels: {
        style: {
          textOverflow: 'none',
          fontSize: '11px',
        },
        formatter: function () {
          return formatters.systemLabel(this.value, 'styled');
        },
      },
    },
		yAxis: {
			min: 0,
			title: {
				text: t.charts.period_total_cost.yAxis.title,
			},
			stackLabels: {
				enabled: true,
				formatter: function() {
					return formatters.periodTotalCost.yAxis(this, series);
        },
				style: {
          visibility: 'visible',
          opacity: '1',
          fontSize: '12px',
					fontWeight: 'bold',
					color: '#333333',
				},
			},
		},
		legend: {
      floating        : false,
			align           : 'right',
			verticalAlign   : 'top',
			backgroundColor : 'white',
			shadow          : false,
      itemStyle: {
        fontSize      : '13px',
      },
		},
		tooltip: {
      formatter: function() {
        return formatters.periodTotalCost.tooltip(this);
      },
      shared: true,
		},
    series: [
      { name: t.charts.period_total_cost.series.solar_saving,
        data: series.solarSavingDatas,
        dataLabels: {
          formatter: function() {
            return (this.y == 0)
              ? ''
              : '-' + toLocaleNumber(parseInt(this.y)) + ' ' + t.charts.period_total_cost.tooltip.devise;
          },
        },
        color: colors[3],
      },
      { name  : t.charts.period_total_cost.series.energy_cost,
      	data  : series.energyCostDatas,
        color : colors[2],
      },
      { name  : t.charts.period_total_cost.series.investment,
      	data  : series.investmentDatas,
        color : colors[1],
      },
      { name  : t.charts.period_total_cost.series.maintenance,
      	data  : series.maintenanceDatas,
        color : colors[0],
      },
    ],
  });
}


//------------------------------------------------------------------------------
//  PERIOD COST EVOLUTION
//------------------------------------------------------------------------------

function _buildPeriodCostEvolution(values) {
  return values.map( (value, i) => {
    // Set graph colors depending on the EnR ratio of the solution
    const color = (value.enrProdRatio < 0.5)
      ? nonEnRColors[i]
      : enRColors[i];

    return {
      name  : value.label,
      color : color,
      data  : value.priceEvolutionsSerie,
    };
  });
}

export function periodCostEvolution(scenario, values) {
  const today = new Date();

  return Highcharts.chart(scenario + '-period-cost-evolution-chart', {
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
  	title: {
  		text: null,
  	},
  	subtitle: {
  		text: null,
  	},
  	yAxis: {
  		title: {
  			text: t.charts.period_cost_evolution.yAxis.title,
  		},
  	},
  	legend: {
  		layout        : 'horizontal',
  		align         : 'right',
  		verticalAlign : 'bottom',
      itemStyle: {
        fontWeight: 'normal',
      },
      labelFormatter: function () {
        return formatters.systemLabel(this.name, 'styled');
      },
  	},
    tooltip: {
      formatter: function() {
        return formatters.periodCostEvolution.tooltip(this);
      },
      shared: true,
    },
    colors: colors,
  	plotOptions: {
      line: {
          marker: {
              enabled: false,
          },
      },
  		series: {
  			pointStart: today.getFullYear(),
  		},
  	},
    series: _buildPeriodCostEvolution(values),
  });
}


//------------------------------------------------------------------------------
//  ENVIRONMENTAL IMPACT
//------------------------------------------------------------------------------

function _buildEnvironmentalImpact(values) {
  let labels             = [];
  let primaryEnergyDatas = [];
  let greenHouseGazDatas = [];

  values.forEach( value => {
    labels.push(value.label);
    primaryEnergyDatas.push(value.yearlyPrimaryEnergy);
    greenHouseGazDatas.push(value.yearlyGreenHouseGaz);
  });

  return {
    labels             : labels,
    primaryEnergyDatas : primaryEnergyDatas,
    greenHouseGazDatas : greenHouseGazDatas,
  };
}

export function environmentalImpact(scenario, values) {
  const series = _buildEnvironmentalImpact(values);

  return Highcharts.chart(scenario + '-environmental-impact-chart', {
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      type: 'bar',
    },
    title: {
      text: null,
    },
    subtitle: {
      text: null,
    },
    yAxis: [
      { // Primary xAxis
        title: {
          text: t.charts.environmental_impact.primary_energy.tooltip.unit,
        },
        labels: {
          formatter: function() {
            return formatters.environmentalImpact.yAxis(this);
          },
        },
        opposite: true,
      }, { // Secondary xAxis
        gridLineWidth: 0,
        title: {
          text: t.charts.environmental_impact.greenhouse_gaz.tooltip.unit,
        },
        labels: {
          formatter: function() {
            return formatters.environmentalImpact.yAxis(this);
          },
          style: {
            color      : '#333333',
            fill       : '#333333',
            fontWeight : 'bold',
          },
        },
      },
    ],
    xAxis: {
      title: {
        text: null,
      },
      min: 0,
      categories: series.labels,
      labels: {
        overflow: 'justify',
        formatter: function () {
          return formatters.systemLabel(this.value, 'styled');
        },
      },
    },
    legend: {
      itemStyle: {
        fontWeight: 'normal',
      },
      labelFormatter: function() {
        return formatters.environmentalImpact.legend(this, 'styled');
      },
    },
    tooltip: {
      formatter: function() {
        return formatters.environmentalImpact.tooltip(this);
      },
      shared: true,
    },
    plotOptions: {
      bar: {
        dataLabels: {
          enabled: true,
          formatter: function() {
            return formatters.environmentalImpact.plotOptions(this);
          },
        },
      },
    },
    series: [
      { name  : 'primary_energy',
        yAxis : 0,
        color : '#764532',
        data  : series.primaryEnergyDatas,
      },
      { name  : 'greenhouse_gaz',
        yAxis : 1,
        color : '#bcde1b',
        data  : series.greenHouseGazDatas,
      },
    ],
  });
}


//------------------------------------------------------------------------------
//  ENERGIES PRICE VARIATIONS
//------------------------------------------------------------------------------

function _getMaxValueOfEnergyPrice(energies) {
  let priceVariations = energies.map(energy => energy.priceVariations);
  priceVariations = [].concat.apply([], priceVariations);
  return parseInt(Math.max.apply(0, priceVariations)) + 1;
}

function energiesPriceVariationsXAxisTicks() {
  const minYear = datas.energies_yearly_price_variations[0].year;
  const maxYear = datas.energies_yearly_price_variations.slice(-1)[0].year;
  let ticks = [minYear];
  for (let i=minYear+2; i <= maxYear; i++) { // skip 2 years to have enough space to display the year
    if (i%10 == 0) ticks.push(i);
  }
  ticks.push(maxYear);
  return ticks;
}

export function energiesPriceVariations(scenario, energies) {
  const yAxisMax = _getMaxValueOfEnergyPrice(energies);

  return Highcharts.chart(scenario + '-energies-yearly-price-variations-chart', {
    chart: {
      spacingLeft: 0,
      marginRight: 20,
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
      fallbackToExportServer: false,
    },
  	title: {
  		text: null,
  	},
  	subtitle: {
  		text: null,
  	},
    xAxis: {
      tickPositions: energiesPriceVariationsXAxisTicks(),
    },
  	yAxis: {
  		title: {
  			text: t.charts.energies_yearly_price_variations.yAxis.title,
  		},
      max: yAxisMax,
      tickInterval: 5,
      endOnTick: false,
  	},
  	legend: {
  		layout: 'horizontal',
  		align: 'left',
      verticalAlign: 'bottom',
      itemStyle: {
        fontWeight: 'normal',
      },
      labelFormatter: function() {
        const _this = this;
        const energyPriceVariation = energies.find( energy => energy.identifier == _this.name).meanYearlyPriceVariation;
        return formatters.energiesPriceVariations.legend(this, energyPriceVariation, 'styled');
      },
  	},
    tooltip: {
      formatter: function() {
        return formatters.energiesPriceVariations.tooltip(this);
      },
      shared: true,
    },
  	plotOptions: {
      line: {
        marker: {
            enabled: false,
        },
      },
  		series: {
  			pointStart: datas.energies_yearly_price_variations[0].year,
        dataLabels:{
          align: 'right',
          x: 0,
          y: 10,
        },
  		},
  	},
  	series: energies.map( energy => {
      return {
        name: energy.identifier,
        color: energyColors[energy.identifier],
        data: energy.priceVariations,
        tooltip: {
          valueSuffix: t.charts.energies_yearly_price_variations.tooltip.devise,
        },
      };
    }),
  });
};



// WEBPACK FOOTER //
// ./assets/javascripts/datas/_charts_config.js